import React, { useMemo, useState, useCallback } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { FlaggedPostResponseSchema } from '@/api/client';
import { PostContent, type MediaInfo } from './PostContent';
import { DataTable } from './DataTable';
import { ImageModal } from './ImageModal';
import { VideoPlayerModal } from '@/feature/play-video/VideoPlayerModal';
import { useAdminContext } from '../contexts/AdminProvider';

interface Props {
  flaggedPosts: FlaggedPostResponseSchema[];
  isLoading: boolean;
  onHidePost: (postId: number) => void;
  onBanUser: (userId: number) => void;
  onUnbanUser: (userId: number) => void;
}

const columnHelper = createColumnHelper<FlaggedPostResponseSchema>();

export function FlaggedPostsView({
  flaggedPosts,
  isLoading,
  onHidePost,
  onBanUser,
  onUnbanUser,
}: Props) {
  const { showConfirmation } = useAdminContext();
  const [activeMedia, setActiveMedia] = useState<MediaInfo | null>(null);

  const handleMediaClick = useCallback((media: MediaInfo) => {
    setActiveMedia(media);
  }, []);

  const closeMedia = useCallback(() => {
    setActiveMedia(null);
  }, []);

  const handleHidePost = useCallback(
    (postId: number) => {
      showConfirmation('Are you sure you want to hide this post?', () =>
        onHidePost(postId)
      );
    },
    [showConfirmation, onHidePost]
  );

  const handleBanUser = useCallback(
    (userId: number) => {
      showConfirmation('Are you sure you want to ban this user?', () =>
        onBanUser(userId)
      );
    },
    [showConfirmation, onBanUser]
  );

  const handleUnbanUser = useCallback(
    (userId: number) => {
      showConfirmation('Are you sure you want to unban this user?', () =>
        onUnbanUser(userId)
      );
    },
    [showConfirmation, onUnbanUser]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('post_id', {
        header: 'ID',
        cell: (info) => (
          <span className="admin-mono">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor((row) => row.post.username, {
        id: 'username',
        header: 'User',
      }),
      columnHelper.accessor((row) => row.post.post_type, {
        id: 'post_type',
        header: 'Type',
        cell: (info) => (
          <span className="admin-badge admin-badge-neutral">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor((row) => row.post.title, {
        id: 'title',
        header: 'Title',
        cell: (info) => info.getValue() || <span style={{ color: 'var(--admin-fg-faint)' }}>-</span>,
      }),
      columnHelper.accessor((row) => row.post.message_text, {
        id: 'message',
        header: 'Message',
        cell: (info) => {
          const value = info.getValue();
          if (!value) return <span style={{ color: 'var(--admin-fg-faint)' }}>-</span>;
          return (
            <span
              style={{
                display: 'block',
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={value}
            >
              {value}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'content',
        header: 'Media',
        cell: (info) => (
          <PostContent
            post={info.row.original.post}
            onMediaClick={handleMediaClick}
          />
        ),
      }),
      columnHelper.accessor('reason', {
        header: 'Reason',
        cell: (info) => (
          <span className="admin-badge admin-badge-warning">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('flagged_at', {
        header: 'Flagged',
        cell: (info) => (
          <span className="admin-mono" style={{ fontSize: '11px' }}>
            {new Date(info.getValue()).toLocaleString()}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <div className="admin-actions">
            <button
              className="admin-btn admin-btn-secondary admin-btn-sm"
              onClick={() => handleHidePost(info.row.original.id)}
            >
              Hide Post
            </button>
            <button
              className="admin-btn admin-btn-danger admin-btn-sm"
              onClick={() => handleBanUser(info.row.original.post.user_id)}
            >
              Ban User
            </button>
            <button
              className="admin-btn admin-btn-ghost admin-btn-sm"
              onClick={() => handleUnbanUser(info.row.original.post.user_id)}
            >
              Unban User
            </button>
          </div>
        ),
      }),
      columnHelper.accessor('hidden_by', {
        header: 'Hidden By',
        cell: (info) => info.getValue() || <span style={{ color: 'var(--admin-fg-faint)' }}>-</span>,
      }),
    ],
    [handleMediaClick, handleHidePost, handleBanUser, handleUnbanUser]
  );

  return (
    <>
      <DataTable
        data={flaggedPosts}
        columns={columns}
        isLoading={isLoading}
        loadingMessage="Loading flagged posts..."
      />

      {activeMedia?.type === 'image' && (
        <ImageModal
          src={activeMedia.src}
          alt="Post content"
          onClose={closeMedia}
        />
      )}

      {activeMedia?.type === 'video' && (
        <VideoPlayerModal
          isOpen={true}
          videoUrl={activeMedia.src}
          onClose={closeMedia}
          playing={true}
          controls={true}
        />
      )}

      {activeMedia?.type === 'audio' && (
        <AudioModal src={activeMedia.src} onClose={closeMedia} />
      )}
    </>
  );
}

function AudioModal({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div
        className="admin-modal"
        style={{ maxWidth: '360px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal-header">
          <h3 className="admin-modal-title">Audio Player</h3>
        </div>
        <div className="admin-modal-body" style={{ padding: '24px' }}>
          <audio src={src} controls autoPlay style={{ width: '100%' }}>
            Your browser does not support the audio element.
          </audio>
        </div>
        <div className="admin-modal-footer">
          <button className="admin-btn admin-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
